/**
 * God View "view as" (screens/admin/GodView.tsx) exit strip. Renders nothing
 * outside an impersonation session. See lib/auth/session.ts's
 * `impersonation` store field, which is non-null for the life of one.
 *
 * Deliberately outside .shell's grid (mounted as AppShell's own first child,
 * not inside the shell div) so it never fights the rail/topbar/content areas.
 * It is a strip ABOVE the whole shell, always visible, never a toast that
 * can be missed or a nav item that can be scrolled past.
 */
import { useNavigate } from 'react-router';
import { useAccount } from '../../lib/auth/use-app-data';
import { useSessionStore, stopImpersonation } from '../../lib/auth/session';
import { apiFetch } from '../../lib/http';
import { t } from '../../lib/i18n/t';
import { captureEvent } from '../../lib/analytics/posthog';
import '../../styles/components/impersonation-banner.css';

export function ImpersonationBanner() {
  const impersonation = useSessionStore((s) => s.impersonation);
  const account = useAccount();
  const navigate = useNavigate();

  if (!impersonation) return null;

  async function exit() {
    captureEvent('admin_impersonation_stopped', { target_account_id: account.id });
    try {
      await apiFetch('/api/v1/admin/impersonate/stop', { method: 'POST' });
    } catch {
      // Best-effort: the session may have already expired server-side.
      // Still restore the admin's own token either way.
    }
    stopImpersonation();
    navigate('/admin/god-view');
  }

  return (
    <div className="impersonation-banner" role="status">
      <span className="impersonation-banner__text">
        {t('Viewing as {email}, as an admin.', { email: account.email })}
      </span>
      <button type="button" className="impersonation-banner__exit" onClick={exit}>
        {t('Exit')}
      </button>
    </div>
  );
}
